import { useState, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { PageHero } from "@/components/bits";
import Lightbox from "@/components/Lightbox";
import { PORTFOLIO_ITEMS } from "@/data/site";

const Portfolio = () => {
  const cats = useMemo(() => ["All", ...new Set(PORTFOLIO_ITEMS.map((p) => p.cat))], []);
  const [active, setActive] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const filtered = active === "All" ? PORTFOLIO_ITEMS : PORTFOLIO_ITEMS.filter((p) => p.cat === active);

  return (
    <>
      <PageHero
        eyebrow="Portfolio"
        title={<>Work that speaks <span className="text-red-500">from the stage</span></>}
        sub="Dance, performances, events, stage shows, training and academy moments — a visual record of our journey. Select any image to view it full-screen."
      />

      <section className="mx-auto max-w-[1440px] px-5 pb-24 sm:px-8" data-testid="portfolio-grid">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Portfolio categories">
          {cats.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={active === c}
              onClick={() => setActive(c)}
              className={`border px-5 py-2.5 font-display text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all ${
                active === c ? "border-red-600 bg-red-600 text-white" : "border-white/15 text-zinc-400 hover:border-red-500/60 hover:text-white"
              }`}
              data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>

        <motion.div layout className="mt-10 columns-1 gap-4 sm:columns-2 lg:columns-3">
          <AnimatePresence mode="popLayout">
            {filtered.map((p, i) => (
              <motion.button
                layout
                key={p.img + p.cat}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="img-zoom group relative mb-4 block w-full overflow-hidden border border-white/10 break-inside-avoid"
                onClick={() => setLightbox(i)}
                aria-label={`View ${p.caption}`}
                data-testid={`portfolio-item-${i}`}
              >
                <img src={p.img} alt={p.caption} className={`w-full object-cover ${i % 3 === 0 ? "aspect-[3/4]" : i % 3 === 1 ? "aspect-square" : "aspect-[4/5]"}`} loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" aria-hidden="true" />
                <div className="absolute bottom-0 left-0 translate-y-2 p-5 text-left opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.24em] text-red-400">{p.cat}</p>
                  <p className="mt-1 text-sm font-semibold text-white">{p.caption}</p>
                </div>
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>

        <p className="mt-12 text-center font-mono2 text-[10px] uppercase tracking-[0.28em] text-zinc-500">
          Photographs from the academy's own events and sessions are added here by the academy.
        </p>
      </section>

      <Lightbox items={filtered} index={lightbox} onClose={() => setLightbox(null)} onNavigate={setLightbox} />
    </>
  );
};

export default Portfolio;
